import React, { useState } from 'react';
import { useLocation } from 'react-router';
import { useAsync } from 'react-use';
import { Heading, Wrapper } from './style';
import ChartArea from '../../Components/ChartArea';
import TableArea from '../../Components/TableArea';
import { ChartFilters } from 'models/request';
import { createFiltersObject } from 'routes/ControlTower/utils';
import { SearchParams } from 'routes/ControlTower/interfaces';
import { ChartData } from 'routes/ControlTower/interfaces';
import { Tabs } from 'components/Tabs';

interface ChartPageProps {
  data?: ChartData[];
  loadCharts: (params: ChartFilters) => Promise<void>;
  searchParams?: SearchParams;
  message?: string;
  setMessage: (value: string) => void;
}

const ChartContent = ({ data }: { data: ChartData }) => {
  const [checkboxedRowNames, setCheckboxedRowNames] = useState<string[]>();
  const [selectedName, setSelectedName] = useState<string>();

  const { series, chartType } = data || {};
  if (!series) return null;

  return (
    <>
      <ChartArea
        series={series}
        checkboxedRowNames={checkboxedRowNames}
        chartType={chartType}
        selectedName={selectedName}
      />

      <TableArea
        series={series}
        checkboxedRowNames={checkboxedRowNames}
        chartType={chartType}
        setCheckboxedRowNames={setCheckboxedRowNames}
        selectedName={selectedName}
        setSelectedName={setSelectedName}
      />
    </>
  );
};

const Chart = ({ loadCharts, data, searchParams, message, setMessage }: ChartPageProps) => {
  const location = useLocation();

  const { idCharts } = (location?.state as { idCharts: number[]; categoryId: number }) || {};

  useAsync(async () => {
    const params = createFiltersObject(searchParams);

    if (idCharts && idCharts.length) {
      setMessage('');
      loadCharts({ ...params, idCharts });
    }
  }, [idCharts]);

  // If no data or only one chart, render without tabs
  if (!data || data.length === 0) {
    return message ? <Wrapper>{message}</Wrapper> : null;
  }

  if (data.length === 1) {
    const chart = data[0];
    return (
      <Wrapper>
        <Heading>{chart.description}</Heading>
        <ChartContent data={chart} />
      </Wrapper>
    );
  }

  // Multiple charts: use tabs
  return (
    <Wrapper>
      <Tabs defaultTab={0}>
        <Tabs.TabList>
          {data.map((chart, index) => (
            <Tabs.Tab key={chart.id} id={index} label={chart.name}>
              {chart.name}
            </Tabs.Tab>
          ))}
        </Tabs.TabList>

        <Tabs.TabPanels hasBorder>
          {data.map((chart, index) => (
            <Tabs.Panel key={chart.id} id={index} label={chart.name} description={chart.description}>
              <ChartContent data={chart} />
            </Tabs.Panel>
          ))}
        </Tabs.TabPanels>
      </Tabs>
    </Wrapper>
  );
};

export default Chart;
